import { useTranslation, Translation } from 'react-i18next';
import React, { useMemo, useState, useCallback, memo, ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import cx from 'clsx';
import message from 'antd/es/message';
import produce from 'immer';
import { UseQueryResult } from 'react-query';

import {
  formatNetworth,
  formatNum,
  getOfficialPath,
  login,
  openNewWindow,
  useCurrentUserId,
} from '@/utils';
import {
  Button,
  HelperTooltip,
  Segmented,
  UserName,
  UnionAvatar,
  EmptyRecord,
  UserCardPopup,
  UnionFollowButtonV2,
} from '@/components';
import { useSwitch, useTheme } from '@/hooks';
import { isOfficalAddress } from '@/utils';

import {
  invalidArticleDetail,
  useArticleTopDepositors,
  useArticleTopEaners,
  useArticleUserDepositor,
  useArticleUserEaner,
} from '../../hooks';
import { isTipComplete, isTipOvertime } from '../../utils';
import { ArticleDepositor, ArticleEarner, FeedArticle } from '../../types';
import { DepositModal } from './DepositModal';
import { DepositorsModal } from '../DepositorsModal';
import style from './index.module.scss';

type ArticleTipProps = {
  article?: FeedArticle;
  articleId: number;
};

const USERS_VIEW: Array<{
  label: ReactNode;
  value: string;
  userLabel: ReactNode;
  userListHook: (id: number) => UseQueryResult<any>;
  userHook: (id: number, currentAddress?: string) => UseQueryResult<any>;
}> = [
  {
    label: <Translation>{t => t('ArticleTip.13p6rw2')}</Translation>,
    value: 'depositors',
    userLabel: <Translation>{t => t('ArticleTip.deposit')}</Translation>,
    userListHook: useArticleTopDepositors,
    userHook: useArticleUserDepositor,
  },
  {
    label: <Translation>{t => t('ArticleTip.1u1v3vy')}</Translation>,
    value: 'earners',
    userLabel: <Translation>{t => t('ArticleTip.reward')}</Translation>,
    userListHook: useArticleTopEaners,
    userHook: useArticleUserEaner,
  },
];

export const ArticleTip = ({ article, articleId }: ArticleTipProps) => {
  const { t } = useTranslation();

  const currentAddress = useCurrentUserId();
  const { on, turnOn, turnOff } = useSwitch();
  const { isDark } = useTheme();
  const [view, setView] = useState(USERS_VIEW[0]);
  const articleDepositUsdValueDecimal = useMemo(
    () => (article ? formatNetworth(article.reward_usd_value) || '$0' : '-'),
    [article],
  );

  // 不能充值了
  const isOvertime = useMemo(
    () => !!article?.deposit_deadline && isTipOvertime(article.deposit_deadline),
    [article?.deposit_deadline],
  );

  const isComplete = useMemo(() => {
    return article?.create_at && isTipComplete(article?.create_at);
  }, [article?.create_at]);

  const isSettled = useMemo(() => !!article?.is_reward_settled, [article?.is_reward_settled]);

  const { data: depositorRes } = useArticleTopDepositors(articleId);

  const viewOptions = useMemo(() => {
    return produce(USERS_VIEW, draft => {
      draft[0].label =
        t('ArticleTip.13p6rw2') +
        ` (${depositorRes?.total_count ? formatNum(depositorRes.total_count, 0) : '0'})`;
    });
  }, [depositorRes, t]);

  const handleViewChanged = useCallback((_, item) => {
    setView(item);
  }, []);

  const status = useMemo(() => {
    return isSettled
      ? {
          text: t('ArticleTip.settled'),
          className: style['is-settled'],
        }
      : isComplete
        ? {
            text: t('ArticleTip.complete'),
            className: style['is-end'],
          }
        : {
            text: t('ArticleTip.in-progress'),
            className: style['is-progress'],
          };
  }, [isSettled, isComplete, t]);

  const handleDepositClick = useCallback(() => {
    if (!currentAddress) {
      login();

      return;
    }

    if (!article) {
      return;
    }

    if (isTipOvertime(article.deposit_deadline) || isTipComplete(article.create_at)) {
      message.info(t('ArticleTip.overtime'));
      invalidArticleDetail(article.id, currentAddress);

      return;
    }

    turnOn();
  }, [turnOn, currentAddress, article, t]);

  return (
    <>
      <div className={style.container}>
        <div className={style.rewardPool}>
          <div className={style.rewardPoolTitle}>{t('ArticleTip.1dzok19')}</div>
          <div className={style.rewardUsdValue}>{articleDepositUsdValueDecimal}</div>
          {isComplete || !article ? null : (
            <HelperTooltip title={isOvertime ? t('ArticleTip.overtime') : null}>
              <Button
                type="primary"
                disabled={isOvertime}
                className={style.depositBtn}
                onClick={handleDepositClick}>
                {t('ArticleTip.1h8kn7i')}
              </Button>
            </HelperTooltip>
          )}
          {article ? (
            <div className={cx(style.tipStatus, status.className, isDark && style.isDark)}>
              {status.text}
            </div>
          ) : null}
        </div>
        <Segmented
          color="dialog"
          hasSplit={false}
          options={viewOptions}
          value={view.value}
          onChange={handleViewChanged}
          radius={21}
          className={style.streamTab}
        />
        <UserList id={articleId} view={view} key={view.value} />
      </div>

      {on ? <DepositModal id={articleId} turnOff={turnOff} /> : null}
    </>
  );
};

const UserList = ({ id, view }: { id: number; view: (typeof USERS_VIEW)[number] }) => {
  const { t } = useTranslation();

  const currentAddress = useCurrentUserId();
  const { userListHook, userHook, userLabel } = view;
  const { data: list, isLoading: isListLoading } = userListHook(id);
  const { data: user, isLoading: isUserLoading } = userHook(id, currentAddress);
  const {
    on: isDepositorModal,
    turnOn: showDepositorModal,
    turnOff: hideDepositorModal,
  } = useSwitch();

  const users = useMemo(() => {
    return (
      (
        list as {
          depositors: ArticleDepositor[];
          total_count: number;
        }
      )?.depositors ||
      (
        list as {
          earners: ArticleEarner[];
        }
      )?.earners
    );
  }, [list]);

  return (
    <>
      <div>
        {isListLoading ? (
          <div>
            <SkeletonList />
          </div>
        ) : users?.length ? (
          <>
            <div className={style.listHeader}>
              <span>{t('ArticleTip.1k157')}</span>
              <span>{userLabel}</span>
            </div>
            {users?.map((x, i) => <UserRow data={x} key={i} />)}
          </>
        ) : (
          <EmptyRecord
            content={
              <span className={style.emptyDesc}>
                {t('ArticleTip.1y9', { context: view.value })}
              </span>
            }
            iconType="user"
            className={style.emptyList}
          />
        )}
        {currentAddress ? (
          <>
            <div className={style.mineTitle}>{t('ArticleTip.1ws', { context: view.value })}</div>
            {isUserLoading ? <SkeletonList count={1} /> : user ? <UserRow data={user} /> : null}
          </>
        ) : null}
      </div>
      {view.value === 'depositors' && (list as any)?.total_count >= 5 ? (
        <div className={style.depositorsLink} onClick={showDepositorModal}>
          {t('ArticleTip.rhq8ne')}
        </div>
      ) : null}
      {isDepositorModal ? (
        <DepositorsModal on={isDepositorModal} turnOff={hideDepositorModal} articleId={id} />
      ) : null}
    </>
  );
};

const UserRow = memo(({ data }: { data: ArticleDepositor | ArticleEarner }) => {
  const handleUserClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      const userId = data.id;
      const isOffical = isOfficalAddress(userId);

      openNewWindow(
        isOffical
          ? `${getOfficialPath(data as OfficialAccount)}/stream`
          : `/profile/${userId}/stream`,
        '_blank',
      );
    },
    [data],
  );

  return (
    <div className={style.userItem}>
      <div onClick={handleUserClick} className={style.avatar}>
        <UnionAvatar data={data} size={32} userCardPopup />
      </div>
      <div className={style.userName} onClick={handleUserClick}>
        {isOfficalAddress(data.id) ? (
          <UserCardPopup address={data.id}>
            <div className={style.officialId}>{(data as OfficialAccount).name}</div>
          </UserCardPopup>
        ) : (
          <UserCardPopup address={data.id}>
            <UserName
              className={style.uid}
              address={data.id}
              name={(data as GeneralAccount).desc!.user.web3_id}
              userDesc={(data as GeneralAccount)?.desc}
            />
          </UserCardPopup>
        )}
        {data?.id ? (
          <UnionFollowButtonV2 address={data?.id} isFollowing={data?.is_following} />
        ) : null}
      </div>
      <div className={style.usdValue}>
        {(data as ArticleDepositor)._rewardUsdValue || (data as ArticleEarner)._earnUsdValue}
      </div>
    </div>
  );
});

const SkeletonList = memo(({ count = 5 }) => (
  <>
    {Array(count)
      .fill(0)
      .map((_, i) => (
        <div className={style.userItem} key={i}>
          <Skeleton circle height={32} width={32} className={style.avatar} />
          <div className={style.userName}>
            <Skeleton width={80} />
          </div>
          <Skeleton width={50} className={style.contribution} />
        </div>
      ))}
  </>
));
